import type { IconType } from 'react-icons';
import {
  Alert,
  AlertIcon,
  Button,
  Box,
  Flex,
  GridItem,
  Icon,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiActivity,
  FiBriefcase,
  FiClipboard,
  FiCreditCard,
  FiFileText,
  FiGitMerge,
  FiHome,
  FiInfo,
  FiLayers,
  FiMapPin,
  FiShield,
  FiTruck,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import { FaPaw } from 'react-icons/fa';
import { useQuery } from '@tanstack/react-query';
import { apiGetContactPolicyInformation } from 'api/acturis/client-center';
import type { ActurisContactPolicy } from 'api/acturis/client-center';
import { policyInformationGroups } from './policyInformation';
import { formatPolicyDate } from './policyGroups';

type Field = { label: string; value?: string | null; wide?: boolean };
type DetailTab = { label: string; icon: IconType; description: string };

const detailTabs = (line: string): DetailTab[] => {
  if (line === 'CL AUTO')
    return [
      {
        label: 'Units',
        icon: FiTruck,
        description: 'Insured vehicles and unit details',
      },
      {
        label: 'Coverages',
        icon: FiShield,
        description: 'Coverage limits, deductibles and endorsements',
      },
    ];
  if (/AUTO/.test(line))
    return [
      {
        label: 'Vehicle',
        icon: FiTruck,
        description: 'Vehicle, garaging, use, value and coverage details',
      },
      {
        label: 'Driver',
        icon: FiUser,
        description: 'Driver information, licensing and insurance details',
      },
      {
        label: 'Assignment',
        icon: FiGitMerge,
        description: 'Vehicle / driver assignment',
      },
    ];
  if (line === 'CL PROP')
    return [
      {
        label: 'Location',
        icon: FiMapPin,
        description: 'Insured locations and property details',
      },
      {
        label: 'Coverages',
        icon: FiShield,
        description: 'Coverage limits, deductibles and endorsements',
      },
    ];
  if (line === 'PL HOME')
    return [
      {
        label: 'Location(s)',
        icon: FiMapPin,
        description: 'Insured locations and dwelling details',
      },
      {
        label: 'General',
        icon: FiHome,
        description: 'General property information',
      },
      {
        label: 'Additional',
        icon: FiLayers,
        description: 'Additional property information and coverages',
      },
    ];
  if (line === 'PL LIFE')
    return [
      {
        label: 'Coverage',
        icon: FiShield,
        description: 'Life insurance coverage and benefits',
      },
      {
        label: 'Beneficiaries',
        icon: FiUsers,
        description: 'Beneficiary details and allocations',
      },
    ];
  if (line === 'PL PET')
    return [
      { label: 'Pet', icon: FaPaw, description: 'Insured pet details' },
      {
        label: 'Coverage',
        icon: FiShield,
        description: 'Pet insurance coverage and benefits',
      },
    ];
  return [];
};

const FieldGroup = ({
  title,
  icon,
  fields,
}: {
  title: string;
  icon: IconType;
  fields: Field[];
}) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const fieldBg = useColorModeValue('#f4f5f7', 'navy.900');
  const border = useColorModeValue('gray.100', 'gray.600');
  return (
    <Box>
      <Flex align="center" gap="2" color={muted} mb="3">
        <Icon as={icon} />
        <Text as="h5" fontSize="xs" fontWeight="700" textTransform="uppercase">
          {title}
        </Text>
      </Flex>
      <SimpleGrid
        as="dl"
        columns={{ base: 1, sm: 2, lg: 4 }}
        spacingX="3"
        spacingY="3"
      >
        {fields.map(({ label, value, wide }) => (
          <GridItem
            key={label}
            colSpan={wide ? { base: 1, sm: 2 } : 1}
            minW="0"
          >
            <Text
              as="dt"
              fontSize="11px"
              fontWeight="600"
              textTransform="uppercase"
              color={muted}
              mb="1"
            >
              {label}
            </Text>
            <Box
              as="dd"
              m="0"
              bg={fieldBg}
              borderWidth="1px"
              borderColor={border}
              borderRadius="md"
              px="3"
              py="2"
              minH="38px"
              fontSize="sm"
              overflowWrap="anywhere"
            >
              {value || (
                <Text as="span" aria-label="Not available">
                  —
                </Text>
              )}
            </Box>
          </GridItem>
        ))}
      </SimpleGrid>
    </Box>
  );
};

const PolicyDetails = ({
  customerId,
  policy,
  versions,
  lineOfBusiness,
}: {
  customerId: string;
  policy: ActurisContactPolicy;
  versions: ActurisContactPolicy[];
  lineOfBusiness: string;
}) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const border = useColorModeValue('gray.100', 'gray.600');
  const headerBg = useColorModeValue('#f9f9fa', 'navy.900');
  const infoBg = useColorModeValue('#e8f2f8', 'navy.900');
  const accent = useColorModeValue('#24658a', 'blue.200');
  const tabs: DetailTab[] = [
    {
      label: 'Policy Information',
      icon: FiClipboard,
      description: 'Summary, term, broker & carrier',
    },
    ...detailTabs(lineOfBusiness),
    {
      label: 'Claims',
      icon: FiShield,
      description: 'Claims associated with this policy',
    },
    {
      label: 'Billing',
      icon: FiCreditCard,
      description: 'Billing method, payment plan and payment details',
    },
    {
      label: 'Transactions',
      icon: FiActivity,
      description: 'Policy versions and transaction history',
    },
  ];
  const canLoad = Boolean(
    customerId && policy.policy_ref && policy.version_ref,
  );
  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [
      'client-center-policy-information',
      customerId,
      policy.policy_ref,
      policy.version_ref,
    ],
    queryFn: () =>
      apiGetContactPolicyInformation(
        customerId,
        policy.policy_ref,
        policy.version_ref!,
      ),
    enabled: canLoad,
    staleTime: 30 * 1000,
    retry: false,
  });
  const groups = policyInformationGroups(
    policy,
    data,
    lineOfBusiness,
    customerId,
  );

  return (
    <Box>
      <Flex
        align="flex-start"
        gap="3"
        bg={infoBg}
        color={accent}
        borderRadius="lg"
        px="4"
        py="3"
        mb="3"
        fontSize="sm"
      >
        <Icon as={FiInfo} mt="0.5" flexShrink={0} />
        <Text>
          Policy details are read-only. Fields marked “—” have no available
          data.
        </Text>
      </Flex>
      <Tabs variant="unstyled" isLazy>
        <TabList
          gap="1"
          borderBottomWidth="2px"
          borderColor={border}
          overflowX="auto"
          aria-label={`Policy ${policy.policy_number || policy.policy_ref} details`}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.label}
              flexShrink={0}
              gap="2"
              px="5"
              py="3"
              bg={headerBg}
              color={accent}
              fontSize="sm"
              fontWeight="600"
              borderWidth="1px"
              borderBottom="0"
              borderColor={border}
              borderTopRadius="xl"
              _selected={{ bg: '#24658a', color: 'white' }}
              _focus={{ boxShadow: 'inset 0 0 0 2px #90cdf4' }}
            >
              <Icon as={tab.icon} />
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab.label} p="0" pt="4">
              <Box
                borderWidth="1px"
                borderColor={border}
                borderRadius="xl"
                overflow="hidden"
              >
                <Flex
                  gap="3"
                  align="center"
                  px="5"
                  py="4"
                  bg={headerBg}
                  borderBottomWidth="1px"
                  borderColor={border}
                >
                  <Flex
                    boxSize="40px"
                    flexShrink={0}
                    align="center"
                    justify="center"
                    bg="#24658a"
                    color="white"
                    borderRadius="lg"
                  >
                    <Icon as={tab.icon} boxSize="20px" />
                  </Flex>
                  <Box>
                    <Text as="h4" fontWeight="700">
                      {tab.label}
                    </Text>
                    <Text fontSize="xs" color={muted} mt="1">
                      {tab.description}
                    </Text>
                  </Box>
                </Flex>
                <Stack p={{ base: '4', md: '5' }} spacing="5">
                  {tab.label === 'Policy Information' ? (
                    <>
                      {isFetching && (
                        <Text role="status" fontSize="sm">
                          Loading policy information…
                        </Text>
                      )}
                      {isError && (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          <Text flex="1">
                            Additional policy information could not be loaded.
                            List details are still shown.
                          </Text>
                          <Button
                            size="sm"
                            ml="3"
                            onClick={() => refetch()}
                            isLoading={isFetching}
                          >
                            Retry
                          </Button>
                        </Alert>
                      )}
                      {!canLoad && (
                        <Text fontSize="sm">
                          Additional information is unavailable because the
                          policy version reference is missing.
                        </Text>
                      )}
                      {groups.map((group) => (
                        <FieldGroup
                          key={group.title}
                          title={group.title}
                          icon={
                            group.title === 'Broker' ||
                            group.title === 'Advisor'
                              ? FiBriefcase
                              : group.title === 'Carrier'
                                ? FiHome
                                : FiFileText
                          }
                          fields={group.fields}
                        />
                      ))}
                    </>
                  ) : tab.label === 'Transactions' ? (
                    <>
                      <Text color={muted} fontSize="sm">
                        Invoice details are not available yet. Available policy
                        versions are shown below.
                      </Text>
                      {versions.map((version, index) => (
                        <Box key={version.version_ref || index}>
                          <FieldGroup
                            title={`${version.business_event_detail || `Version ${index + 1}`} · ${version.policy_status_detail || 'Status unavailable'}`}
                            icon={FiActivity}
                            fields={[
                              {
                                label: 'Version reference',
                                value: version.version_ref,
                              },
                              {
                                label: 'Insured',
                                value: version.policyholder_name,
                              },
                              {
                                label: 'Effective date',
                                value: formatPolicyDate(version.effective_date),
                              },
                              {
                                label: 'Expiry date',
                                value: formatPolicyDate(version.expiry_date),
                              },
                            ]}
                          />
                          {version.latest_policy_status_detail &&
                            version.policy_status_detail &&
                            version.latest_policy_status_detail !==
                              version.policy_status_detail && (
                              <Text fontSize="xs" color={muted} mt="3">
                                Acturis version status:{' '}
                                {version.policy_status_detail}. Reported latest
                                status: {version.latest_policy_status_detail}.
                              </Text>
                            )}
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Flex
                      direction="column"
                      align="center"
                      textAlign="center"
                      py="8"
                      color={muted}
                      gap="3"
                    >
                      <Icon as={tab.icon} boxSize="28px" />
                      <Text fontSize="sm">
                        {tab.label} details are not available yet.
                      </Text>
                    </Flex>
                  )}
                </Stack>
              </Box>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default PolicyDetails;
